/** @jsx jsx */

import { PropsWithChildren, useEffect } from 'react';

import { Drawer } from '@zapier/editor-core';
import { jsx } from '@emotion/core';
import { usePrevious } from '@zapier/common-hooks';

import { VersionStatus } from 'app/developer-v3/constants';
import {
  useAvroEvent,
  useQueryImplementationCheck,
  useVersionLifecycle,
} from 'app/developer-v3/hooks';

import Guide from './Guide';
import Settings from './Settings';
import Validate from './Validate';
import Versioning from './Versioning';
import { getTabs, normalizeMode } from './utils';
import { COMMON_SECTION_IDS, MODES, TABS, interactionGoals } from './constants';

import type { PropertiesType } from './types';
import type { SemVer } from 'app/developer-v3/types/version';
import { VERSIONING_SECTION_IDS } from '.';

const BREAKPOINT = '(max-width: 1440px)';
const DRAWER_WIDTH = 450;

type Props = {
  appId: string;
  isPublished: boolean;
  isStaff: boolean;
  mode: MODES;
  tabActive: TABS;
  update: (properties: PropertiesType) => void;
  version: SemVer;
  versionStatus: VersionStatus;
};

const SidebarRight = ({
  appId,
  children,
  isPublished,
  isStaff,
  mode,
  tabActive,
  update,
  version,
  versionStatus,
}: PropsWithChildren<Props>) => {
  const { emitPartnerUserInteractionEvent } = useAvroEvent();

  const tabActivePrevious = usePrevious(tabActive);

  const { data, isFetched, isLoading } = useQueryImplementationCheck({
    integrationId: Number(appId),
    mode: normalizeMode(mode),
    version,
  });
  const failedChecks = data?.totalFailures ?? 0;
  const hasValidated = !isLoading && isFetched;

  useEffect(() => {
    if (tabActivePrevious !== tabActive && tabActive) {
      emitPartnerUserInteractionEvent({
        eventAction: 'open',
        interactionGoal:
          (tabActive === TABS.VALIDATE && hasValidated
            ? interactionGoals.validated
            : interactionGoals[tabActive]) ?? undefined,
        interactionName: 'open_right_sidebar',
        interactionRawText: tabActive,
        optionSelected: tabActive,
      });
    }
  }, [
    emitPartnerUserInteractionEvent,
    hasValidated,
    tabActive,
    tabActivePrevious,
  ]);

  const versionLifecycle = useVersionLifecycle(appId);

  const onCloseTab = () => {
    update({
      mode: MODES.DEFAULT,
      tabActive: TABS.NONE,
      activeSubsection: COMMON_SECTION_IDS.NONE,
    });
  };

  const isSettingsTabVisible = isStaff;
  const isLifecycleTabVisible =
    tabActive === TABS.VERSIONING || versionLifecycle.isPending;
  const isValidateTabVisible = !versionLifecycle.isLifecyclePending;
  const isVersionLifecycleCompletedPrevious = usePrevious(
    versionLifecycle.isDone
  );

  if (
    tabActive === TABS.VERSIONING &&
    !isVersionLifecycleCompletedPrevious &&
    versionLifecycle.isDone
  ) {
    onCloseTab();
  }

  return (
    <Drawer.Container breakpoint={BREAKPOINT}>
      <Drawer.Sibling>{children}</Drawer.Sibling>
      <Drawer
        breakpoint={BREAKPOINT}
        formatTabItemCount={false}
        onTabChanged={tab => {
          // This is intentionally dumb. If we find we need more
          // tab specific handling of subsections, we can add it
          // at that time.
          const activeSubsection =
            tab === TABS.VERSIONING && versionLifecycle.lifecycleType
              ? VERSIONING_SECTION_IDS[
                  versionLifecycle.lifecycleType.toUpperCase()
                ]
              : COMMON_SECTION_IDS.NONE;

          update({
            activeSubsection,
            tabActive: tab as TABS,
          });
        }}
        openTabId={tabActive}
        tabs={getTabs({
          failedChecks,
          isLifecycleTabVisible,
          isSettingsTabVisible,
          isValidateTabVisible,
        })}
        widthDesktop={DRAWER_WIDTH}
        widthMobile={DRAWER_WIDTH}
        zIndex={10}
      >
        <Drawer.TabContent tabId={TABS.GUIDE}>
          <Guide onClose={onCloseTab} />
        </Drawer.TabContent>
        {isValidateTabVisible && (
          <Drawer.TabContent tabId={TABS.VALIDATE}>
            <Validate
              appId={appId}
              isPublished={isPublished}
              mode={mode}
              onClose={onCloseTab}
              version={version}
              versionStatus={versionStatus}
            />
          </Drawer.TabContent>
        )}
        {isSettingsTabVisible && (
          <Drawer.TabContent tabId={TABS.SETTINGS}>
            <Settings onClose={onCloseTab} />
          </Drawer.TabContent>
        )}
        {isLifecycleTabVisible && (
          <Drawer.TabContent tabId={TABS.VERSIONING}>
            <Versioning integrationId={appId} onClose={onCloseTab} />
          </Drawer.TabContent>
        )}
      </Drawer>
    </Drawer.Container>
  );
};

SidebarRight.defaultProps = {
  mode: MODES.DEFAULT,
  tabActive: TABS.NONE,
  guideActiveSubsection: COMMON_SECTION_IDS.NONE,
};

export default SidebarRight;
